import { CButton, CInput, CInputGroup, CInputGroupAppend, CLabel, CModal, CModalBody, CModalFooter, CModalHeader } from '@coreui/react';
import * as React from 'react';
import { Table } from 'reactstrap';
import { ControlTypeEnum, IDispatcher, IGenericList, IGenericListItem, IGetDynListParams, IServiceMessage } from '../../abstraction';
import { AppLabels } from '../../core/AppLabels';
import { Utils } from '../../core/utils';
import { IActionModel, KActionList } from '../KActionList';

interface IKSelectModel {
    Value: string;
    Disable: boolean;
    Visible: boolean;
    HasFocus: boolean;
    ControlType: ControlTypeEnum;
    Key: string;
    ButtonListColor: string;
    ShowAsButtonList: boolean;
    ButtonListVerticalOrientation: boolean;
    InputInlineStyle: string;
    Pattern: string;
    ListNoSelect: boolean;
}

interface IProps {
    Control: IKSelectModel;
    onChange: (value: string) => void;
    getViewstate?: () => IGetDynListParams;
    onClick?: () => void;
    onItemSelected?: (item: IGenericListItem) => void;
    dispatcher: IDispatcher;
}

interface IState {
    showList: boolean;
    list: IGenericListItem[];
    selectedItem: string;
    virtualKeyboard: boolean;
    ascending: number;
    columnSort: number;
    classSort: string;
}

// Représente une zone de saisie type liste de choix
export class KSelect extends React.Component<IProps, IState> {

    inputRef: any;

    public constructor(props: any) {
        super(props);

        this.state = {
            list: [],
            showList: false,
            selectedItem: this.props.Control.Value,
            virtualKeyboard: this.props.dispatcher.isVirtualKeyboardActive,
            ascending: 1,
            columnSort: 1,
            classSort: "sorted_ascending",
        }

        this.inputRef = React.createRef();

        // Permet de mettre à jour le mode du clavier virtuel en cas de changement
        this.props.dispatcher.onVirtualKeyboardChanged.add((state) => { this.setState({ virtualKeyboard: state }) });
    }
    //#region React Hacks

    // Désabonnement de l'évènement 
    componentWillUnmount = () => {
        this.props.dispatcher.onVirtualKeyboardChanged.reset();
    }

    // Une fois l'affichage effectué, on regarde si la zone est censée avoir le focus,
    // Si oui, on le donne via la référence à l'élément html (React ne le permettant pas)
    componentDidUpdate = () => {
        if (this.props.Control.HasFocus) {
            if (this.inputRef.current) this.inputRef.current.focus();
        }
    }

    //#endregion 

    //#region Events

    // On change = changement de valeur de la zone
    // On transmet simplement l'évènement au dessus.
    // C'est pas notre guerre
    //
    // event : évènement associé
    onChange = (event) => {
        if (this.props.Control.Disable) return;
        if (this.props.Control.ControlType == ControlTypeEnum.select) return;
        if (this.props.onChange) this.props.onChange(event);
    }

    // Transfert du onclick au responsable
    onClick = () => {
        if (this.props.onClick) this.props.onClick();
    }

    // Sélection d'un élément de liste
    // row : élement sélectionné
    onItemSelected = (row: IGenericListItem) => {
        this.setState({ selectedItem: row.Key });
        this.toggle();

        if (this.props.onItemSelected) this.props.onItemSelected(row);
    }

    //#endregion

    //#region Fonctions métier

    // Demande d'ouverture de la fenêtre de sélection
    openPopup = async () => {
        // Récupère le contrôle qui nous est associé
        // on passe par une variable pour alléger le traitement
        let ctrl = this.props.Control;
        if (ctrl.Disable) return;

        try {
            // Deux 
            if (ctrl.ControlType.toString() == ControlTypeEnum.selectDynamic.toString()) {
                let params: IGetDynListParams = this.props.getViewstate();
                params.kmaZone = ctrl.Key;

                this.props.dispatcher.getDynamicList(params)
                    .then((result: IGenericList) => {
                        this.setState({ list: result.Data, showList: true });
                        this.forceUpdate();
                    })
                    .catch((messages: IServiceMessage[]) => {
                        this.props.dispatcher.MessageBox("", messages[0].message, messages[0].level);
                    });
            }
            else {
                this.props.dispatcher.getGenericList(ctrl.Key)
                    .then((result: IGenericList) => {
                        this.setState({
                            list: result.Data,
                            showList: true
                        });
                        this.forceUpdate();
                    })
                    .catch((messages: IServiceMessage[]) => {
                        this.props.dispatcher.MessageBox("", messages[0].message, messages[0].level);
                    });
            }
        }
        catch (error) {
            console.log(error);
        }
    }

    toggle = () => {
        this.setState({
            showList: false
        });
    }

    //#endregion 

    //#region Rendus

    onClose = (choice: IActionModel) => {
        if (choice != null) {
            this.setState({ selectedItem: choice.Key });
            if (this.props.onItemSelected) {
                let item = this.state.list.filter(c => c.Key == choice.Key)[0];
                if (item) this.props.onItemSelected(item);
            }
        }
        this.setState({ showList: false });

    }
    onHeaderClick = (column: number) => {
        if (column == this.state.columnSort) {
            this.setState({ ascending: this.state.ascending*-1, classSort: this.state.ascending==-1 ? "sorted_ascending" : "sorted_descending" }) // inversion du sens de tri
        } else {
            this.setState({ columnSort: column }) // changement de colonne
        }
    }

    renderPopup = () => {
        let choices: IActionModel[] = [];
        this.state.list.map(c => {
            choices.push({
                Key: c.Key,
                Text: c.Value,
                Color: this.props.Control.ButtonListColor,
                Class: "",
                Icon: "",
                InlineStyle: "",
                InnerCSS: "",
                VisibilityCond: "",

            });

        });

        if (this.props.Control.ShowAsButtonList) {
            return (
                <KActionList
                    imageProvider={this.props.dispatcher}
                    vertical={this.props.Control.ButtonListVerticalOrientation}
                    isOpen={this.state.showList}
                    title={AppLabels.selectListTitle}
                    choices={choices}
                    closeCallBack={this.onClose} />
            );
        }

        let keySort = this.state.columnSort == 1 ? "Key" : "Value";

        let numericList: boolean = true;
        if (this.state.list.length > 0) {
            for (let n = 0; n < this.state.list.length && numericList == true; n++) {
                if (isNaN(this.state.list[n][keySort])) numericList = false;
            }
        }

        let sortedList: IGenericListItem[];
        if (numericList) {
            sortedList = this.state.list.sort((a, b) => Number(a[keySort]) > Number(b[keySort]) ? this.state.ascending : -this.state.ascending);
        }
        else {
            sortedList = this.state.list.sort((a, b) => a[keySort] > b[keySort] ? this.state.ascending : -this.state.ascending);
        }
        


        return (
            <CModal centered closeOnBackdrop={false} className="KControl KControlSelect " show={this.state.showList} onClose={this.toggle} >
                <CModalHeader>
                    {AppLabels.selectListTitle}
                </CModalHeader>
                <CModalBody >
                    <Table className="TableHead" size="sm">
                        <thead><tr>
                            <th className={"KTableHeaderCol"} onClick={() => this.onHeaderClick(1)}>
                                <div className="thContent">{AppLabels.listCol1Title}
                                    <div className={this.state.columnSort == 1 ? this.state.classSort : ""} />
                                </div>
                            </th>
                            <th className={"KTableHeaderCol"} onClick={() => this.onHeaderClick(2)}>
                                <div className="thContent">{AppLabels.listCol2Title}
                                    <div className={this.state.columnSort == 2 ? this.state.classSort : ""} />
                                </div>
                            </th>
                        </tr>
                        </thead>
                    </Table>
                    <div className="TableWrapper">
                        <Table size="sm">
                            <tbody >{
                                sortedList.map((row, rowIndex) => {
                                    return (
                                        <tr onClick={() => { this.onItemSelected(row) }} key={rowIndex} className={(this.state.selectedItem == row.Key) ? " selected" : ""} >
                                            <td >
                                                <CLabel>{row.Key}</CLabel>
                                            </td>
                                            <td >
                                                <CLabel>{row.Value}</CLabel>
                                            </td>
                                        </tr>
                                    );
                                })
                            }
                            </tbody>
                        </Table>
                    </div>
                </CModalBody>
                <CModalFooter>
                    <CButton color="primary" onClick={this.toggle}>{AppLabels.ok}</CButton>
                </CModalFooter>
            </CModal>
        );
    }

    render() {
        let ctrl = this.props.Control;
        if (ctrl == null) return null;
        if (!ctrl.Visible) return null;

        let disable = ctrl.Disable;

        let popup = this.renderPopup();

        let inlineStyle = Utils.decodeInlineStyleFromJson(ctrl.InputInlineStyle);

        let addon = (ctrl.Disable || ctrl.ListNoSelect==true) ? "" : <CInputGroupAppend readOnly={disable} ><CButton color="secondary" onClick={this.openPopup} >...</CButton></CInputGroupAppend>

        return (
            <CInputGroup>
                {popup}
                <CInput
                    style={inlineStyle}
                    readOnly={disable} innerRef={this.inputRef}
                    name={this.props.Control.Key}
                    color="default"
                    value={ctrl.Value}
                    onChange={this.onChange}
                    onClick={this.onClick}
                    pattern={this.props.Control.Pattern}
                    inputMode={!this.state.virtualKeyboard ? "none" : "text"}
                />
                {addon}
            </CInputGroup>
        )
    }

    //#endregion
}